import { Languages } from 'lucide-react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button, Menu, MenuItem } from '@/components/ui';
import i18n from '@/i18n.json';
import { currentPathInLocale, localeRoot } from '@/utils/locale-path';

const ONE_YEAR = 60 * 60 * 24 * 365;

/** Remembers an explicit language choice so Next.js stops auto-detecting on "/". */
export const rememberLocale = (locale) => {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not available in all supported browsers
  document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=${ONE_YEAR}; samesite=lax`;
};

/**
 * Hook behind the language menus: the locales, the current one, and a link
 * to the current page in each locale.
 *
 * Until `refresh()` runs in the browser the links point at each locale's home
 * page, which is identical on the server and the first client render (the
 * exact path depends on the browser URL, so it must not be rendered earlier).
 */
export const useLocaleLinks = () => {
  const router = useRouter();
  const [hrefs, setHrefs] = useState(null);
  const current = router?.locale ?? i18n.defaultLocale;

  const refresh = () => {
    setHrefs(
      Object.fromEntries(i18n.locales.map((locale) => [locale, currentPathInLocale(locale)]))
    );
  };

  return {
    current,
    locales: i18n.locales,
    hrefFor: (locale) => hrefs?.[locale] ?? localeRoot(locale),
    refresh,
  };
};

/**
 * Language menu listing every locale in src/i18n.json. Switching keeps the
 * current page (translated URL segments included).
 *
 *   <LanguageSwitcher />                              // icon button
 *   <LanguageSwitcher showLabel align="start" side="top" />   // footer
 */
const LanguageSwitcher = ({ align = 'end', className, showLabel = false, side = 'bottom' }) => {
  const t = useTranslations('nav');
  const lT = useTranslations('languages');
  const { current, locales, hrefFor, refresh } = useLocaleLinks();

  return (
    <Menu
      align={align}
      className={className}
      onOpenChange={(open) => open && refresh()}
      side={side}
      renderTrigger={(triggerProps) => (
        <Button
          $size={showLabel ? 'lg' : 'icon'}
          $variant="ghost"
          aria-label={showLabel ? undefined : t('changeLanguage')}
          title={t('changeLanguage')}
          type="button"
          {...triggerProps}
        >
          <Languages size={20} strokeWidth={1.75} />
          {showLabel && <span>{lT(current)}</span>}
        </Button>
      )}
    >
      {locales.map((locale) => (
        <MenuItem
          checked={locale === current}
          href={hrefFor(locale)}
          hrefLang={locale}
          key={locale}
          lang={locale}
          onSelect={() => rememberLocale(locale)}
        >
          {lT(locale)}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default LanguageSwitcher;
